import React, { PureComponent } from "react";
import Link from "next/link";
import Icon from "@mdi/react";
import { mdiLock } from "@mdi/js";
import { Collapse } from "reactstrap";

import styles from "./styles.scss";
import TeamLogo from "../../Team/TeamLogo";
import { PICK_TYPES, BetQLService, BetQLSportItem } from "src/common-services";
import { renderRating } from "../../../services/ValueDataService";
import { COMP_GREEN, COMP_RED } from "../../../colors";
import { TrackingProperties } from "../../../constants";
import TrendDrawer from "./TrendDrawer";
import PublicDrawer from "./PublicDrawer";
import ProgressBar from "./ProgressBar";
import LinkWithHandler from "../../LinkWithHandler";
import { SubscriptionContext } from "../../../services/SubscriptionService";
import AnalyticsService from "../../../services/AnalyticsService";
import MarketingVideo from "src/components/MarketingVideo";
import GameLines from "src/components/GameLines";
import { useGameProgressAndChannel } from "src/services/EventService";
import TrendModelDrawer from "./TrendModelDrawer";
import pluralize from "pluralize";

export interface GameMobileRowProps {
  style?: any;
  game: any;
  filterKey: string;
  pageKey: string;
  currentSportObj: BetQLSportItem;
  periodFilter: string;
  trackingProps: TrackingProperties;
  columnOrder: number[];
  open: boolean;
  drawerIndex?: number;
}

export interface GameMobileRowState {
  open: boolean;
  drawerIndex?: number;
}

const drawer = {
  // STATS: 0,
  TRENDS: 0,
  PUBLIC: 1,
  SHARP: 2,
  BEST_LINE: 3,
};

const PICK_MAP: any = {
  0: "Away",
  1: "Home",
  2: "Draw",
};

class GameMobileRow extends PureComponent<GameMobileRowProps & { progressAndChannel: string }, GameMobileRowState> {
  static contextType = SubscriptionContext;
  analyticsObject: any;
  constructor(props: GameMobileRowProps & { progressAndChannel: string }) {
    super(props);
    this.state = {
      open: props.open,
      drawerIndex: props.drawerIndex,
    };
    this.analyticsObject = {
      screen: "GamesView",
      page: props.pageKey,
      betType: props.filterKey,
      sport: props.currentSportObj.name,
    };
  }

  getBold = (atHome: "Home" | "Away" | "Draw", teamName?: boolean) => {
    const { game, pageKey, filterKey, currentSportObj } = this.props;
    const hasAccess = game.freeGame || this.context.hasSubscriptionAccess(currentSportObj.key);
    const { homeTeam, awayTeam, trendsPick } = game;

    const periodFilter =
      filterKey === "spread" || filterKey === "moneyline" || filterKey === "total" ? `FG${filterKey}` : filterKey;
    // only bold if it's freeGame or hasSubscription
    if (hasAccess && pageKey !== "movement") {
      switch (pageKey) {
        case "sharp":
          if (filterKey.includes("total")) {
            return !teamName && atHome === "Away" && game[`sharp${periodFilter}Display`] !== "--";
          } else {
            return (
              (atHome === "Home" && game[`sharp${periodFilter}IsHome`] === true) ||
              (atHome === "Away" && game[`sharp${periodFilter}IsHome`] === false) ||
              (atHome === "Draw" && game[`sharp${periodFilter}IsHome`] === null)
            );
          }
        case "public":
          if (filterKey.includes("total")) {
            return !teamName && atHome === "Away" && game[`sharp${periodFilter}TicketDisplay`] !== "--";
          } else {
            return (
              (atHome === "Home" && game[`sharp${periodFilter}TicketIsHome`] === true) ||
              (atHome === "Away" && game[`sharp${periodFilter}TicketIsHome`] === false) ||
              (atHome === "Draw" && game[`sharp${periodFilter}TicketIsHome`] === null)
            );
          }
        case "value":
          const isNew = currentSportObj[`has${periodFilter}PowerRating`];
          const trend = game[`max${isNew ? "New" : ""}${periodFilter}Trend`];
          if (filterKey.includes("total")) {
            if (
              isNew &&
              (!game[`value${periodFilter}PreferredTotal`] || game[`value${periodFilter}PreferredTotal`] === "push")
            ) {
              return false;
            }
            return teamName !== undefined ? !teamName : trend !== null;
          } else if (trend) {
            if (atHome === "Home") {
              return trend && trend.teamId === homeTeam.id;
            } else if (atHome === "Away") {
              return trend && trend.teamId === awayTeam.id;
            } else if (atHome === "Draw") {
              return trend && trend.teamId === null;
            }
          } else {
            const period = filterKey === "spread" || filterKey === "moneyline" ? `FG${filterKey}` : filterKey;
            const awayData = game[`current${period}AwayData`];
            const homeData = game[`current${period}HomeData`];
            const drawData = game[`current${period}DrawData`];
            if (awayData !== null && homeData !== null) {
              const lines = [awayData, homeData];
              if (drawData !== null) {
                lines.push(drawData);
              }
              const pickIndex = lines.indexOf(Math.min(...lines));
              if (pickIndex >= 0) {
                return atHome === PICK_MAP[pickIndex];
              }
            }
          }
          break;
        case "trend":
          return trendsPick.favoredTeam === atHome.toLowerCase();
      }
    }
    return false;
  };

  renderInfo = (atHome: "Home" | "Away" | "Draw", main?: boolean) => {
    const { game, pageKey, filterKey, currentSportObj } = this.props;
    const hasAccess = game.freeGame || this.context.hasSubscriptionAccess(currentSportObj.key);
    // don't render the second line column if we aren't on the movement pageKey
    // OR don't render the home column if we are looking at TOTAL lines
    const isTotalBet = filterKey.includes("total");
    const isTrendBet = pageKey === "trend";

    if ((pageKey !== "movement" && !main) || (atHome === "Home" && isTotalBet && !isTrendBet)) {
      return null;
    } else {
      let displayText;
      // determin the period and betType
      const periodFilter =
          filterKey === "spread" || filterKey === "moneyline" || filterKey === "total" ? `FG${filterKey}` : filterKey,
        // sports that use the new power trends for spread
        isNew = currentSportObj[`has${periodFilter}PowerRating`],
        // get the trend to show
        trend = game[`max${isNew ? "New" : ""}${periodFilter}Trend`],
        // if we are on the movment page, lets grab the line type
        displayLine = pageKey === "movement" && main ? "open" : "current",
        // do we have public data, this check will be used multiple times
        noPublicPercentage = game[`sharp${periodFilter}TicketDisplay`] === "--",
        // do we have sharp data, this check will be used multiple times
        noSharpPercentage = game[`sharp${periodFilter}Display`] === "--";
      // special case for total values
      if (filterKey.includes("total")) {
        // get the actual line value to display
        const totalDisplay = game[`${displayLine}${periodFilter}AwayDisplay`];
        const preferredTotal = game[`value${periodFilter}PreferredTotal`];
        // determine if data supports Over or Under based on page type
        if (pageKey === "value" && preferredTotal && preferredTotal !== "push") {
          displayText = !hasAccess
            ? totalDisplay
            : game[`value${periodFilter}PreferredTotal`] === "under"
            ? `U ${totalDisplay}`
            : `O ${totalDisplay}`;
        } else if (pageKey === "public") {
          displayText =
            noPublicPercentage || !hasAccess
              ? totalDisplay
              : game[`sharp${periodFilter}TicketIsHome`]
              ? `U ${totalDisplay}`
              : `O ${totalDisplay}`;
        } else if (pageKey === "sharp") {
          displayText = noSharpPercentage
            ? totalDisplay
            : game[`sharp${periodFilter}IsHome`]
            ? `U ${totalDisplay}`
            : `O ${totalDisplay}`;
        } else if (isTrendBet) {
          const prefix = totalDisplay !== "--" ? (atHome === "Home" ? "U " : "O ") : "";
          displayText = `${prefix}${totalDisplay}`;
        } else {
          displayText = totalDisplay;
        }
      } else {
        displayText = game[`${displayLine}${periodFilter}${atHome}Display`];
      }

      const { favoredTeam } = game.trendsPick;

      // GREAN ARROW LOGIC
      const showGreenArrow =
        // show the arrow if the user has access (free game or paid)
        hasAccess &&
        // make sure this is actually the data we want to highlight
        this.getBold(atHome) &&
        // show the arrow if its the value page and we have data
        ((pageKey === "value" &&
          trend &&
          !!trend.displayRating &&
          (!isTotalBet ||
            (isTotalBet &&
              game[`value${periodFilter}PreferredTotal`] &&
              game[`value${periodFilter}PreferredTotal`] !== "push"))) ||
          // show the arrow if its the public page and we have data
          (pageKey === "public" && !noPublicPercentage) ||
          // show the arrow if its the sharp page, they have access and we have data
          (pageKey === "sharp" && !noSharpPercentage) ||
          (pageKey === "trend" && favoredTeam));

      const classNameForDisplayText = this.getBold(atHome)
        ? "games-table-mobile-row__bold-body-text"
        : "games-table-mobile-row__body-text";

      return (
        <div
          style={{
            flexDirection: "row",
            ...(pageKey !== "movement" && main ? {} : { justifyContent: "center" }),
          }}
          className="games-table-mobile-row__flex-item"
        >
          {main && pageKey !== "movement" ? (
            <div
              style={{
                borderLeftColor: showGreenArrow ? COMP_GREEN : "transparent",
              }}
              className="games-table-mobile-row__green-arrow"
            />
          ) : (
            <div />
          )}
          <div className={classNameForDisplayText}>{displayText}</div>
        </div>
      );
    }
  };

  renderMovementHeader = () => {
    const { pageKey } = this.props;
    if (pageKey !== "movement") {
      return;
    } else {
      return (
        <div className="games-table-mobile-row__row-container">
          <div className="games-table-mobile-row__flex-item" style={{ flex: 1 }}>
            <div />
          </div>
          <div className="games-table-mobile-row__flex-item" style={{ alignItems: "center" }}>
            Open
          </div>
          <div className="games-table-mobile-row__flex-item" style={{ alignItems: "center" }}>
            Current
          </div>
        </div>
      );
    }
  };

  renderRightSide = () => {
    const { game, pageKey, filterKey, currentSportObj } = this.props;
    const hasAccess = game.freeGame || this.context.hasSubscriptionAccess(currentSportObj.key);
    const periodFilter =
      filterKey === "spread" || filterKey === "moneyline" || filterKey === "total" ? `FG${filterKey}` : filterKey;
    let lineMoveSpacer;
    if (game.league.name === "MLB") {
      lineMoveSpacer = 17;
    } else {
      lineMoveSpacer = 24;
    }
    switch (pageKey) {
      case "value":
        if (hasAccess) {
          const isNew = currentSportObj[`has${periodFilter}PowerRating`];
          const trend = game[`max${isNew ? "New" : ""}${periodFilter}Trend`];
          return renderRating(trend, false);
        }
        return this.renderUpgradeButton(game, currentSportObj, "Value Game card");
      case "public":
        return (
          <div
            style={{
              justifyContent: "center",
            }}
          >
            <div>
              {hasAccess ? (
                <div className="games-table-mobile-row__percentage-box">
                  <div className="games-table-mobile-row__precentage-box-text">
                    {game[`sharp${periodFilter}TicketDisplay`]}
                  </div>
                </div>
              ) : (
                this.renderUpgradeButton(game, currentSportObj, "Public Game card")
              )}
            </div>
            <div className="games-table-mobile-row__precentage-box-label">Of total Bets</div>
          </div>
        );
      case "sharp":
        return (
          <div
            style={{
              justifyContent: "center",
            }}
          >
            <div>
              {hasAccess ? (
                <div className="games-table-mobile-row__percentage-box">
                  <div className="games-table-mobile-row__precentage-box-text">
                    {game[`sharp${periodFilter}Display`]}
                  </div>
                </div>
              ) : (
                this.renderUpgradeButton(game, currentSportObj, "Sharp Game Card")
              )}
            </div>
            <div className="games-table-mobile-row__precentage-box-label">Money Edge</div>
          </div>
        );
      case "movement":
        return (
          <div>
            <div style={{ height: lineMoveSpacer }} />
            <div
              style={{
                justifyContent: "center",
              }}
            >
              <div>
                {hasAccess ? (
                  <div className="games-table-mobile-row__percentage-box">
                    <div className="games-table-mobile-row__precentage-box-text">
                      {BetQLService.getRecentMove(game, filterKey)}
                    </div>
                  </div>
                ) : (
                  this.renderUpgradeButton(game, currentSportObj, "Line Move Game card")
                )}
              </div>
              <div className="games-table-mobile-row__precentage-box-label">Recent Move</div>
            </div>
          </div>
        );
      default:
        return <div />;
    }
  };

  toggleDrawer = async (drawerIndex: number) => {
    let retValue: any = {};
    if (this.state.drawerIndex === drawerIndex) {
      retValue.open = !this.state.open;
      retValue.drawerIndex = this.state.open ? null : drawerIndex;
      this.setState(retValue);
    } else {
      retValue.open = true;
      retValue.drawerIndex = drawerIndex;
      this.setState(retValue);
    }
  };

  openDrawer = (drawerIndex: number, analyticText: string) => {
    this.toggleDrawer(drawerIndex);
    AnalyticsService.track(analyticText, this.analyticsObject);
  };

  getDrawerButtonStyle = (index: number) => {
    let className = "games-table-mobile-row__drawer-button";
    if (this.state.open) {
      className += " games-table-mobile-row__drawer-button--border";
    }
    if (this.state.drawerIndex === index) {
      className += " games-table-mobile-row__drawer-button--active-border";
    }
    return className;
  };

  getDrawerButtonTextStyle = (index: number) => {
    let className = "games-table-mobile-row__drawer-button-text";
    if (this.state.drawerIndex === index) {
      className += " games-table-mobile-row__drawer-button-text--active";
    }
    return className;
  };

  getPublicTitle = (atHome: "Home" | "Away" | "Draw") => {
    const { homeTeam, awayTeam } = this.props.game;
    if (this.props.filterKey.includes("total")) {
      return atHome === "Home" ? "Under" : "Over";
    } else if (this.props.currentSportObj.hasDraws) {
      if (atHome === "Draw") {
        return "Draw";
      }
      return atHome === "Home" ? homeTeam.preferredAbbreviation : awayTeam.preferredAbbreviation;
    } else {
      return atHome === "Home" ? homeTeam.fullName : awayTeam.fullName;
    }
  };

  renderDrawer = () => {
    const { game, filterKey, currentSportObj, pageKey } = this.props;
    const { homeTeam, awayTeam } = game;
    const hasAccess = game.freeGame || this.context.hasSubscriptionAccess(currentSportObj.key);
    let drawerTitle = "";
    let drawerBody = "";
    switch (this.state.drawerIndex) {
      case drawer.TRENDS:
        drawerTitle = "TopTrends";
        drawerBody = `See who our recommended best bet is for ${game.awayTeam.fullName} @ ${game.homeTeam.fullName}`;
        break;
      case drawer.PUBLIC:
        drawerTitle = "Public Data";
        drawerBody = `See if the public bettors like ${game.awayTeam.fullName} or ${game.homeTeam.fullName} tonight.`;
        break;
      case drawer.SHARP:
        drawerTitle = "Sharp Data";
        drawerBody = `See if the pro bettors like ${game.awayTeam.fullName} or ${game.homeTeam.fullName} tonight.`;
        break;
    }

    const openPricing = () =>
      this.context.redirectToPricing(
        {
          screen: "GamesView",
          page: "Games Table",
          sport: currentSportObj.name,
          source: "Value Game card",
          awayTeam: awayTeam.fullName,
          homeTeam: homeTeam.fullName,
          gameId: game.id,
        },
        drawerTitle + " Drawer"
      );

    const periodFilter =
      filterKey === "spread" || filterKey === "moneyline" || filterKey === "total" ? `FG${filterKey}` : filterKey;
    let DrawerComponent;

    const sportPeriodHasDraw = currentSportObj.hasDraws && periodFilter.includes("moneyline");

    const topPaywall = !hasAccess && <div className="games-table-mobile-row__drawer-body-text">{drawerBody}</div>;

    const bottomPaywall = !hasAccess && (
      <React.Fragment>
        <button
          onClick={openPricing}
          className="games-table-mobile-row__precentage-box-locked-stacked"
          style={{ marginBottom: 20 }}
        >
          <Icon path={mdiLock} size="20px" color="#004198" />
          <div className="games-table-mobile-row__precentage-box-locked-text">Premium Access</div>
        </button>
        <div className="games-table-mobile-row__info-links">
          <Link href="/how-it-works" legacyBehavior>
            <a className="games-table-mobile-row__info-link">More info about Best Bets</a>
          </Link>
          <MarketingVideo className="games-table-mobile-row__marketing-video" />
        </div>
      </React.Fragment>
    );

    switch (this.state.drawerIndex) {
      case drawer.TRENDS:
        DrawerComponent = () => (
          <>
            {pageKey !== "trend" ? (
              <>
                {topPaywall}

                <div className={`${hasAccess ? "games-table-mobile-row__drawer-collapsible-container" : ""}`}>
                  <TrendDrawer
                    currentSport={currentSportObj}
                    filterType={filterKey}
                    game={game}
                    trendTitle={PICK_TYPES[filterKey].shortName}
                    showData={hasAccess}
                  />
                </div>

                {bottomPaywall}
              </>
            ) : (
              <TrendModelDrawer periodFilter={periodFilter} game={game} currentSportObj={currentSportObj} />
            )}
          </>
        );
        break;
      case drawer.PUBLIC:
        DrawerComponent = () => (
          <>
            {topPaywall}
            <div className="games-table-mobile-row__drawer-collapsible-container">
              <PublicDrawer
                awayPercent={game[`sharp${periodFilter}TicketAwayDisplay`]}
                awayTitle={this.getPublicTitle("Away")}
                awayValue={game[`sharp${periodFilter}TicketAwayData`]}
                homePercent={game[`sharp${periodFilter}TicketHomeDisplay`]}
                homeTitle={this.getPublicTitle("Home")}
                homeValue={game[`sharp${periodFilter}TicketHomeData`]}
                drawValue={game[`sharp${periodFilter}TicketDrawData`]}
                drawPercent={game[`sharp${periodFilter}TicketDrawDisplay`]}
                showData={hasAccess}
                showDraw={sportPeriodHasDraw}
              />
              {hasAccess ? (
                <div className="games-table-mobile-row__flex-row">
                  <Link href={`/${currentSportObj.url}/game-predictions/${game.slugId}`} passHref legacyBehavior>
                    <LinkWithHandler
                      className="games-table-mobile-row__more-info-btn"
                      onCustomClick={this._trackGameSelected}
                    >
                      See Full Analysis
                    </LinkWithHandler>
                  </Link>
                </div>
              ) : null}
            </div>
            {bottomPaywall}
          </>
        );
        break;
      case drawer.SHARP:
        DrawerComponent = () => (
          <>
            {topPaywall}
            <div className="games-table-mobile-row__drawer-collapsible-container">
              {hasAccess ? <div className="games-table-mobile-row__drawer-title-text">Sharp Bettor Report</div> : null}
              <div className="games-table-mobile-row__flex-row">
                <div
                  className="games-table-mobile-row__flex-item"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    marginRight: 10,
                  }}
                >
                  <div className="games-table-mobile-row__drawer-subtitle-text">{this.getPublicTitle("Away")}</div>
                  <ProgressBar value={hasAccess ? game[`sharp${periodFilter}PublicAwayData`] : 0} align="left" />
                  <div className="games-table-mobile-row__drawer-text">
                    % of Money {hasAccess ? game[`sharp${periodFilter}PublicAwayDisplay`] : "--"}
                  </div>
                  <ProgressBar value={hasAccess ? game[`sharp${periodFilter}TicketAwayData`] : 0} align="left" />
                  <div className="games-table-mobile-row__drawer-text">
                    % of Tickets {hasAccess ? game[`sharp${periodFilter}TicketAwayDisplay`] : "--"}
                  </div>
                </div>
                {/* Hiding since not implemented in mobile yet */}
                {/* {sportPeriodHasDraw && (
                  <div
                    className="games-table-mobile-row__flex-item"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      marginRight: 10,
                    }}
                  >
                    <div className="games-table-mobile-row__drawer-subtitle-text">
                      {this.getPublicTitle("Draw")}
                    </div>
                    <ProgressBar
                      value={
                        hasAccess ? game[`sharp${periodFilter}PublicDrawData`] : 0
                      }
                      align="left"
                    />
                    <div className="games-table-mobile-row__drawer-text">
                      % of Money{" "}
                      {hasAccess
                        ? game[`sharp${periodFilter}PublicDrawDisplay`]
                        : "--"}
                    </div>
                    <ProgressBar
                      value={
                        hasAccess ? game[`sharp${periodFilter}TicketDrawData`] : 0
                      }
                      align="left"
                    />
                    <div className="games-table-mobile-row__drawer-text">
                      % of Tickets{" "}
                      {hasAccess
                        ? game[`sharp${periodFilter}TicketDrawDisplay`]
                        : "--"}
                    </div>
                  </div>
                )} */}
                <div
                  className="games-table-mobile-row__flex-item"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                  }}
                >
                  <div className="games-table-mobile-row__drawer-subtitle-text">{this.getPublicTitle("Home")}</div>
                  <ProgressBar value={hasAccess ? game[`sharp${periodFilter}PublicHomeData`] : 0} align="right" />
                  <div className="games-table-mobile-row__drawer-text">
                    % of Money {hasAccess ? game[`sharp${periodFilter}PublicHomeDisplay`] : "--"}
                  </div>
                  <ProgressBar value={hasAccess ? game[`sharp${periodFilter}TicketHomeData`] : 0} align="right" />
                  <div className="games-table-mobile-row__drawer-text">
                    % of Tickets {hasAccess ? game[`sharp${periodFilter}TicketHomeDisplay`] : "--"}
                  </div>
                </div>
              </div>
              {hasAccess ? (
                <div className="games-table-mobile-row__more-info-text-neutral" style={{ marginBottom: 10 }}>
                  {BetQLService.getSharpAbbrev(game, filterKey)} Pro Money Advantage by{" "}
                  {game[`sharp${periodFilter}Display`]}
                </div>
              ) : null}
              {hasAccess ? (
                <div className="games-table-mobile-row__flex-row">
                  <Link href={`/${currentSportObj.url}/game-predictions/${game.slugId}`} passHref legacyBehavior>
                    <LinkWithHandler
                      className="games-table-mobile-row__more-info-btn"
                      onCustomClick={this._trackGameSelected}
                    >
                      See Full Analysis
                    </LinkWithHandler>
                  </Link>
                </div>
              ) : null}
            </div>
            {bottomPaywall}
          </>
        );
        break;
      case drawer.BEST_LINE:
        DrawerComponent = () => (
          <div className="games-table-mobile-row__best-lines">
            <GameLines slugId={game.slugId} />
          </div>
        );
        break;
      default:
        DrawerComponent = () => <div />;
    }
    return (
      <div style={{ position: "relative" }}>
        <DrawerComponent />
      </div>
    );
  };

  _trackGameSelected = () => {
    const { game, trackingProps } = this.props;
    if (trackingProps) {
      AnalyticsService.track("Open Game", {
        screen: "GamesView",
        awayTeam: game.awayTeam.fullName,
        homeTeam: game.homeTeam.fullName,
        gameId: game.id,
        ...trackingProps,
      });
    }
  };

  renderUpgradeButton = (game: any, currentSportObj: BetQLSportItem, sourceString: string) => {
    return (
      <button
        onClick={() =>
          this.context.redirectToPricing(
            {
              screen: "GamesView",
              page: "Games Table",
              sport: currentSportObj.name,
              source: sourceString,
              awayTeam: game.awayTeam.fullName,
              homeTeam: game.homeTeam.fullName,
              gameId: game.id,
            },
            `Games Table ${sourceString}`
          )
        }
        className="games-table-mobile-row__percentage-box-locked"
      >
        <Icon className="games-table-mobile-row__lock-icon" path={mdiLock} color="#004198" />
        <div className="games-table-mobile-row__precentage-box-locked-text">Premium Access</div>
      </button>
    );
  };

  render() {
    const { open, drawerIndex } = this.state;
    const { style, game, currentSportObj, filterKey, pageKey, progressAndChannel } = this.props;
    const { homeTeam, awayTeam, awayTeamPitcher, homeTeamPitcher } = game;
    const showDraw = currentSportObj.hasDraws && filterKey.includes("moneyline");
    const isTotalBet = filterKey.includes("total");
    const isTrendBet = pageKey.includes("trend");
    const hasAccess = game.freeGame || this.context.hasSubscriptionAccess(currentSportObj.key);

    const buttonDrawers = [
      {
        text: isTrendBet ? "Top Trends" : "Best Bet",
        analyticText: "Open Top Trends Drawer",
        isVisible: true,
      },
      { text: "Public", analyticText: "Open Public Drawer", isVisible: true },
      {
        text: "Sharp",
        analyticText: "Open Sharp Drawer",
        isVisible: currentSportObj.hasSharpData,
      },
      {
        text: "Best Lines",
        analyticText: "Open Best Lines Drawer",
        isVisible: true,
      },
    ];

    const { awayCount, homeCount, drawCount, favoredTeam } = game.trendsPick;
    const isHomeTrend = favoredTeam === "home";
    const isAwayTrend = favoredTeam === "away";
    const isDrawTrend = favoredTeam === "draw";

    const classNameForAwayTeamName =
      this.getBold("Away", true) && !isTotalBet
        ? "games-table-mobile-row__bold-body-text"
        : "games-table-mobile-row__body-text";

    const classNameForHomeTeamName =
      this.getBold("Home", true) && !isTotalBet
        ? "games-table-mobile-row__bold-body-text"
        : "games-table-mobile-row__body-text";

    const awayRank = game?.awayTeamSeed ?? game?.awayTeam?.teamStats?.rank ?? "";
    const homeRank = game?.homeTeamSeed ?? game?.homeTeam?.teamStats?.rank ?? "";

    return (
      <div className="games-table-mobile-row" style={style}>
        <Link href={`/${currentSportObj.url}/game-predictions/${game.slugId}`} passHref legacyBehavior>
          <LinkWithHandler
            className="games-table-mobile-row__game-info-container"
            onCustomClick={this._trackGameSelected}
          >
            <div style={{ flex: 1 }}>
              {this.renderMovementHeader()}
              <div className="games-table-mobile-row__row-container">
                <div className="games-table-mobile-row__flex-item-row">
                  <TeamLogo
                    containerClassName="games-table-mobile-row__team-image"
                    leagueName={currentSportObj.imagePath}
                    teamAbbreviation={
                      currentSportObj.isTennis ? game.awayTeam.country : game.awayTeam.preferredAbbreviation
                    }
                    size={30}
                  />
                  <div className={classNameForAwayTeamName}>{awayTeam.preferredAbbreviation}</div>
                  {awayRank ? <div className="games-table-mobile-row__rank-text">{awayRank}</div> : null}
                </div>
                {this.renderInfo("Away", true)}
                {this.renderInfo("Away")}
              </div>
              {currentSportObj.hasPitchers && awayTeamPitcher ? (
                <div className="games-table-mobile-row__row-container">
                  <div style={{ width: 41 }} />
                  <div className="games-table-mobile-row__flex-item-row">
                    <div className="games-table-mobile-row__starting-pitcher-text">
                      SP: {awayTeamPitcher.firstName.charAt(0)}. {awayTeamPitcher.lastName}
                      <br />
                      {game.awayTeamPitcher.wins ?? 0}-{game.awayTeamPitcher.losses ?? 0},&nbsp;
                      {game.awayTeamPitcher.earnedRunAvg ?? "--"} ERA
                    </div>
                  </div>
                </div>
              ) : null}
              <div className="games-table-mobile-row__row-container">
                <div className="games-table-mobile-row__flex-item-row">
                  <TeamLogo
                    containerClassName="games-table-mobile-row__team-image"
                    leagueName={currentSportObj.imagePath}
                    teamAbbreviation={
                      currentSportObj.isTennis ? game.homeTeam.country : game.homeTeam.preferredAbbreviation
                    }
                    size={30}
                  />
                  <div className={classNameForHomeTeamName}>{homeTeam.preferredAbbreviation}</div>
                  {homeRank ? <div className="games-table-mobile-row__rank-text">{homeRank}</div> : null}
                </div>
                {this.renderInfo("Home", true)}
                {this.renderInfo("Home")}
              </div>
              {currentSportObj.hasPitchers && homeTeamPitcher ? (
                <div className="games-table-mobile-row__row-container">
                  <div style={{ width: 41 }} />
                  <div className="games-table-mobile-row__flex-item-row">
                    <div className="games-table-mobile-row__starting-pitcher-text">
                      SP: {homeTeamPitcher.firstName.charAt(0)}. {homeTeamPitcher.lastName}
                      <br />
                      {game.homeTeamPitcher.wins ?? 0}-{game.homeTeamPitcher.losses ?? 0},&nbsp;
                      {game.homeTeamPitcher.earnedRunAvg ?? "--"} ERA
                    </div>
                  </div>
                </div>
              ) : null}
              {showDraw ? (
                <div className="games-table-mobile-row__row-container">
                  <div className="games-table-mobile-row__flex-item-row">
                    <div className="games-table-mobile-row__team-image" style={{ width: 30 }} />
                    <div
                      className={`${
                        this.getBold("Draw", true)
                          ? "games-table-mobile-row__bold-body-text"
                          : "games-table-mobile-row__body-text"
                      }`}
                    >
                      Draw
                    </div>
                    <div className="games-table-mobile-row__rank-text" />
                  </div>
                  {this.renderInfo("Draw", true)}
                  {this.renderInfo("Draw")}
                </div>
              ) : null}
              <div className="games-table-mobile-row__header">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "flex-start",
                  }}
                >
                  <div
                    className="games-table-mobile-row__game-time"
                    style={{
                      ...(game.eventState === "POSTPONED"
                        ? {
                            color: COMP_RED,
                            fontWeight: 400,
                          }
                        : {}),
                    }}
                  >
                    {(() => {
                      if (game.eventState === "STARTED" || game.eventState === "FINAL") {
                        return game.period === "TIME" ? "HALF" : game.period;
                      } else if (game.eventState === "POSTPONED") {
                        return "Postponed";
                      } else {
                        return progressAndChannel;
                      }
                    })()}
                  </div>
                  <div className="games-table-mobile-row__live-text">{game.eventState === "STARTED" ? "Live" : ""}</div>
                </div>
              </div>
            </div>
            {isTrendBet ? (
              hasAccess ? (
                <div className="trend-model-count-container">
                  <div className={`team-selected-count ${isAwayTrend ? "selected-count-highlighted" : ""}`}>
                    {pluralize("Trend", awayCount, true)}
                  </div>
                  {currentSportObj.name === "MLB" && awayTeamPitcher ? <div style={{ height: 31 }} /> : null}
                  <div className={`team-selected-count ${isHomeTrend ? "selected-count-highlighted" : ""}`}>
                    {pluralize("Trend", homeCount, true)}
                  </div>

                  {showDraw ? (
                    <div className={`team-selected-count ${isDrawTrend ? "selected-count-highlighted" : ""}`}>
                      {pluralize("Trend", drawCount, true)}
                    </div>
                  ) : null}
                </div>
              ) : (
                this.renderUpgradeButton(game, currentSportObj, "Trend Bet Game Card")
              )
            ) : (
              <div style={{ paddingTop: "3px" }}>{this.renderRightSide()}</div>
            )}
          </LinkWithHandler>
        </Link>
        <div className="games-table-mobile-row__arrow-divider" />
        <div className="games-table-mobile-row__drawer-container">
          <div className="games-table-mobile-row__drawer-button-container">
            {buttonDrawers.map(
              ({ text, analyticText, isVisible }, index) =>
                isVisible && (
                  <button
                    key={index}
                    className={this.getDrawerButtonStyle(index)}
                    onClick={() => this.openDrawer(index, analyticText)}
                  >
                    <div className={this.getDrawerButtonTextStyle(index)}>
                      {drawerIndex === index && open ? "Close" : text}
                    </div>
                  </button>
                )
            )}
          </div>
          <Collapse isOpen={open}>{this.renderDrawer()}</Collapse>
        </div>
        <style jsx global>
          {styles}
        </style>
      </div>
    );
  }
}

const GameMobileRowHoc = (props: GameMobileRowProps) => {
  const progressAndChannel = useGameProgressAndChannel(props.game);
  return <GameMobileRow {...props} progressAndChannel={progressAndChannel} />;
};

export default GameMobileRowHoc;
